/**
 * Пополнение библиотеки (октябрь 2026): чего не хватало целям студии.
 *
 * Цель «Просто предупредить» держалась на двух идеях — добавлены пять
 * предупреждений без стороны: перегрев, паника, аномальный объём,
 * пробуждение тренда и нерешительная свеча на объёме. Плюс направленные
 * идеи, которых не было: MACD через ноль по тренду, откат по тренду со
 * стохастиком, пересечение взвешенных средних и быстрый пробой на объёме.
 *
 * Каждая собрана из индикаторов, которые уже считает движок, и проверена
 * тестом: на обычной истории она даёт сигналы, а не молчит.
 */
import { above, adx, alertOnly, breakOut, close, crossDown, crossUp, ema, happened, ind, level, macdLine, mirror, number, rsi, rvol, spec, stochK, weight, } from './kit.js';
export const MORE_TEMPLATES = [
    /* ---------------------------------------------------------- предупреждения */
    {
        id: 'rsi-heat',
        name: 'Перегрев',
        icon: '🔥',
        category: 'indicators',
        hint: 'RSI поднялся выше 80 — рост зашёл далеко. Не сигнал продавать, а повод посмотреть на график.',
        steps: ['RSI пересёк 80 вверх', 'Объём выше обычного'],
        keywords: ['rsi', 'перегрев', 'перекупленность', 'предупреждение'],
        passport: {
            level: 'novice',
            market: 'any',
            horizon: 'day',
            style: 'alert',
            timeframe: '1H',
            idea: 'Цена росла так быстро, что RSI ушёл выше 80. Такое бывает редко и обычно перед паузой или откатом.',
            why: 'Когда покупают все, покупателей становится меньше: любой повод для фиксации прибыли разворачивает цену.',
            fails: 'В сильном тренде RSI может жить выше 80 неделями — это предупреждение, а не шорт.',
            tip: 'Хорошо работает как оповещение для бота-сетки: при перегреве стоит поднять коридор, а не продавать.',
        },
        build: (o) => spec(o, 'Перегрев', alertOnly({ context: [], trigger: crossUp(rsi, number(80)), confirmations: [weight('c1', above(rvol, number(1.3)), 100)] }), 0),
    },
    {
        id: 'rsi-panic',
        name: 'Паника',
        icon: '🧊',
        category: 'indicators',
        hint: 'RSI упал ниже 20 — продают все разом. Не сигнал покупать, а повод посмотреть на график.',
        steps: ['RSI пересёк 20 вниз', 'Объём выше обычного'],
        keywords: ['rsi', 'паника', 'перепроданность', 'предупреждение'],
        passport: {
            level: 'novice',
            market: 'any',
            horizon: 'day',
            style: 'alert',
            timeframe: '1H',
            idea: 'Цена падала так резко, что RSI ушёл ниже 20. Так выглядит паника: продают, не глядя на цену.',
            why: 'Паника выжигает продавцов: когда продавать больше некому, даже небольшой спрос разворачивает цену.',
            fails: 'В обвале RSI остаётся ниже 20 долго — ловить дно по одному этому сигналу опасно.',
            tip: 'После паники ищите подтверждение: «Дно после падения» в студии подберёт вход.',
        },
        build: (o) => spec(o, 'Паника', alertOnly({ context: [], trigger: crossDown(rsi, number(20)), confirmations: [weight('c1', above(rvol, number(1.3)), 100)] }), 0),
    },
    {
        id: 'volume-anomaly',
        name: 'Аномальный объём',
        icon: '📢',
        category: 'volume',
        hint: 'Объём в три раза выше обычного — на монету пришли. Сторону не называет.',
        steps: ['Объём превысил обычный втрое'],
        keywords: ['объём', 'аномалия', 'всплеск', 'предупреждение'],
        passport: {
            level: 'novice',
            market: 'any',
            horizon: 'day',
            style: 'alert',
            timeframe: '15m',
            idea: 'За одну свечу проторговали втрое больше обычного. Кто-то крупный пришёл или вышла новость.',
            why: 'Объём — след денег. Большие деньги не заходят незаметно, и их приход меняет рынок.',
            fails: 'Всплеск бывает и в конце движения, и в начале — куда пойдёт цена, эта идея не знает.',
            tip: 'То же самое по всему рынку ищет «Радар»: там видно, что было после таких всплесков раньше.',
        },
        build: (o) => spec(o, 'Аномальный объём', alertOnly({ context: [], trigger: crossUp(rvol, number(3)), confirmations: [weight('c1', above(rvol, number(5)), 60), weight('c2', above(adx, number(20)), 40)] }), 0),
    },
    {
        id: 'adx-wake',
        name: 'Тренд просыпается',
        icon: '⏰',
        category: 'trend',
        hint: 'Сила тренда (ADX) поднялась выше 25 после затишья — рынок выбирает направление.',
        steps: ['ADX пересёк 25 вверх', 'Объём выше обычного'],
        keywords: ['adx', 'тренд', 'начало', 'предупреждение'],
        passport: {
            level: 'skilled',
            market: 'any',
            horizon: 'swing',
            style: 'alert',
            timeframe: '4H',
            idea: 'Долго не было тренда, и вот ADX пошёл вверх: одна сторона начала давить.',
            why: 'Тренды рождаются из затишья. Момент, когда сила растёт, — лучшее время решить, куда вы с ним.',
            fails: 'ADX не говорит направление: тренд может проснуться и вниз.',
            tip: 'Посмотрите, где цена относительно EMA 200: выше — просыпается рост, ниже — падение.',
        },
        build: (o) => spec(o, 'Тренд просыпается', alertOnly({ context: [], trigger: crossUp(adx, number(25)), confirmations: [weight('c1', above(rvol, number(1.2)), 100)] }), 0),
    },
    {
        id: 'doji-volume',
        name: 'Нерешительность на объёме',
        icon: '⚖️',
        category: 'price-action',
        hint: 'Свеча без тела — дожи — на большом объёме: силы сравнялись, скоро кто-то победит.',
        steps: ['Свеча-дожи', 'Объём в полтора раза выше обычного'],
        keywords: ['дожи', 'doji', 'свеча', 'нерешительность', 'предупреждение'],
        passport: {
            level: 'novice',
            market: 'any',
            horizon: 'day',
            style: 'alert',
            timeframe: '1H',
            idea: 'Цена открылась и закрылась почти на одном уровне, хотя торговали много. Покупатели и продавцы уравнялись.',
            why: 'Равновесие на большом объёме не держится долго: чья-то сторона сдастся, и цена рванёт.',
            fails: 'Дожи на маленьком объёме ничего не значат — поэтому здесь нужен объём.',
            tip: 'Дожи у пробитого уровня или после долгого движения значит больше, чем посреди коридора.',
        },
        build: (o) => spec(o, 'Нерешительность на объёме', alertOnly({ context: [], trigger: happened(ind('doji', { share: 0.1 })), confirmations: [weight('c1', above(rvol, number(1.5)), 100)] }), 50),
    },
    /* ---------------------------------------------------------- направленные */
    {
        id: 'macd-zero',
        name: 'MACD через ноль по тренду',
        icon: '0️⃣',
        category: 'indicators',
        hint: 'Линия MACD пересекла ноль в сторону крупного тренда — импульс подтвердил направление.',
        steps: ['Цена по ту же сторону EMA 200', 'MACD пересёк ноль'],
        keywords: ['macd', 'ноль', 'тренд', 'импульс'],
        passport: {
            level: 'novice',
            market: 'trend',
            horizon: 'swing',
            style: 'with-trend',
            timeframe: '4H',
            idea: 'Быстрая средняя обогнала медленную настолько, что MACD ушёл за ноль, — и это по ходу большого тренда.',
            why: 'Пересечение нуля позже обычного пересечения линий, зато реже обманывает: импульс уже набран.',
            fails: 'В боковике MACD пилит ноль туда-обратно, и сигналы идут один за другим.',
            tip: 'Добавьте «Только когда есть тренд (ADX)» в мастерской — пилы станет меньше.',
        },
        build: (o) => spec(o, 'MACD через ноль по тренду', mirror((side) => ({ context: [side.beyond(close, ema(200))], trigger: side.cross(macdLine, number(0)), confirmations: [weight('c1', above(adx, number(20)), 60), weight('c2', above(rvol, number(1.2)), 40)] })), 0),
    },
    {
        id: 'stoch-trend-pullback',
        name: 'Откат по тренду со стохастиком',
        icon: '↩️',
        category: 'trend',
        hint: 'Тренд вверх, а стохастик вышел из перепроданности — откат закончился, можно идти с трендом.',
        steps: ['Цена по ту же сторону EMA 100', 'Стохастик вышел из крайней зоны'],
        keywords: ['стохастик', 'откат', 'тренд', 'pullback'],
        passport: {
            level: 'novice',
            market: 'trend',
            horizon: 'day',
            style: 'with-trend',
            timeframe: '1H',
            idea: 'В растущем рынке цена ненадолго откатилась, стохастик упал ниже 20 и вернулся — откат кончился.',
            why: 'Входить в тренд на откате дешевле, чем догонять его на вершине: стоп короче, цель та же.',
            fails: 'Если тренд уже ломается, «откат» оказывается началом разворота — следите за EMA 100.',
            tip: 'На 15-минутках сигналов больше, но и шума: для начала хватит часовых свечей.',
        },
        build: (o) => spec(o, 'Откат по тренду со стохастиком', mirror((side) => ({ context: [side.beyond(close, ema(100))], trigger: side.cross(stochK, number(level(side, 20))), confirmations: [weight('c1', side.beyond(close, ema(200)), 60), weight('c2', above(adx, number(20)), 40)] })), 0),
    },
    {
        id: 'wma-cross',
        name: 'Пересечение взвешенных средних',
        icon: '✖️',
        category: 'classic',
        hint: 'Быстрая взвешенная средняя (10) пересекла медленную (30) при заметной силе тренда.',
        steps: ['WMA 10 пересекла WMA 30', 'ADX выше 20'],
        keywords: ['wma', 'средние', 'пересечение', 'классика'],
        passport: {
            level: 'novice',
            market: 'trend',
            horizon: 'swing',
            style: 'with-trend',
            timeframe: '4H',
            idea: 'Взвешенная средняя сильнее верит последним свечам, поэтому разворачивается раньше обычной.',
            why: 'Пересечение средних — самый старый способ поймать смену тренда; взвешенные дают его на пару свечей раньше.',
            fails: 'Чем раньше сигнал, тем чаще ложный: без силы тренда средние пилят.',
            tip: 'Сравните в «Дуэли» с «Золотым крестом» на вашей монете: кто точнее — тот и ваш.',
        },
        build: (o) => spec(o, 'Пересечение взвешенных средних', mirror((side) => ({ context: [above(adx, number(20))], trigger: side.cross(ind('wma', { period: 10 }), ind('wma', { period: 30 })), confirmations: [weight('c1', side.beyond(close, ema(200)), 60), weight('c2', above(rvol, number(1.2)), 40)] })), 0),
    },
    {
        id: 'quick-breakout-volume',
        name: 'Быстрый пробой на объёме',
        icon: '🚀',
        category: 'breakout',
        hint: 'Цена вышла за экстремум последних 10 свечей, и объём это подтвердил.',
        steps: ['Пробит экстремум 10 свечей', 'Объём заметно выше обычного', 'Тренд набирает силу'],
        keywords: ['пробой', 'объём', 'быстрый', 'внутри дня'],
        passport: {
            level: 'skilled',
            market: 'any',
            horizon: 'day',
            style: 'breakout',
            timeframe: '15m',
            idea: 'Короткий коридор из десяти свечей прорван, и в этот момент торговали сильно больше обычного.',
            why: 'Пробой без объёма — колебание, пробой на объёме — чьё-то решение. За решением часто идёт продолжение.',
            fails: 'Ночью и в выходные объём мал — порог силы отсекает такие пробои, не снижайте его.',
            tip: 'Для бота: короткая цель и стоп за серединой коридора — пробои внутри дня живут недолго.',
        },
        build: (o) => spec(o, 'Быстрый пробой на объёме', mirror((side) => ({
            context: [],
            trigger: breakOut(side, 10),
            confirmations: [weight('c1', above(rvol, number(1.8)), 70), weight('c2', above(adx, number(20)), 30)],
        })), 60),
    },
];
//# sourceMappingURL=more.js.map